import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Users, UserPlus, MessageCircle, Star, Search, X, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Strip empty-string / null optional fields so backend Optional[...] validators
// (e.g. EmailStr) accept the omission instead of rejecting "".
const clean = (obj) => Object.fromEntries(
  Object.entries(obj).filter(([, v]) => v !== '' && v != null)
);

const useOrgScopedHeaders = (token) => {
  const [orgId, setOrgId] = useState(() => {
    try { return localStorage.getItem('current_organization_id') || null; } catch { return null; }
  });
  useEffect(() => {
    if (!token || orgId) return;
    let cancelled = false;
    fetch(`${API_URL}/api/platform/context`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(ctx => {
        if (cancelled || !ctx) return;
        const id = ctx?.organizations?.[0]?.id;
        if (id) { setOrgId(id); try { localStorage.setItem('current_organization_id', id); } catch {} }
      }).catch(() => {});
    return () => { cancelled = true; };
  }, [token, orgId]);
  const headers = useMemo(() => ({
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(orgId ? { 'X-Organization-Id': orgId } : {}),
  }), [token, orgId]);
  return { headers, orgId };
};

const StatCard = ({ label, value, icon: Icon }) => (
  <div className="bg-white rounded-xl p-5 shadow-sm border border-surface-border" data-testid={`crm-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center">
        <Icon className="w-5 h-5 text-primary-500" />
      </div>
      <div>
        <div className="text-2xl font-display text-ink tabular-nums">{value}</div>
        <div className="text-xs text-ink-muted">{label}</div>
      </div>
    </div>
  </div>
);

const CRM = () => {
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const { headers, orgId } = useOrgScopedHeaders(token);

  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', tags: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [consentEvents, setConsentEvents] = useState([]);
  const [consentChoice, setConsentChoice] = useState('GRANTED');
  const [consentSource, setConsentSource] = useState('customer_request');
  const [consentReference, setConsentReference] = useState('');
  const [consentError, setConsentError] = useState(null);
  const [savingConsent, setSavingConsent] = useState(false);
  const [interaction, setInteraction] = useState({ type: 'note', summary: '', channel: '' });

  const load = useCallback(async () => {
    if (!headers['X-Organization-Id']) { setLoading(false); return; }
    setLoading(true);
    try {
      const qs = search ? `?search=${encodeURIComponent(search)}` : '';
      const [cRes, sRes] = await Promise.all([
        fetch(`${API_URL}/api/crm/customers${qs}`, { headers }),
        fetch(`${API_URL}/api/crm/stats`, { headers }),
      ]);
      setCustomers(cRes.ok ? await cRes.json() : []);
      setStats(sRes.ok ? await sRes.json() : null);
    } finally { setLoading(false); }
  }, [headers, search]);

  useEffect(() => { load(); }, [load]);

  const createCustomer = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFormError(null);
    try {
      const body = clean({ ...form, tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : undefined });
      const res = await fetch(`${API_URL}/api/crm/customers`, { method: 'POST', headers, body: JSON.stringify(body) });
      if (res.ok) {
        setShowForm(false);
        setForm({ name: '', email: '', phone: '', tags: '', notes: '' });
        load();
      } else {
        const err = await res.json().catch(() => ({}));
        setFormError(typeof err.detail === 'string' ? err.detail : 'Could not create customer. Check the fields.');
      }
    } catch {
      setFormError('Network error. Please try again.');
    } finally { setSaving(false); }
  };

  const openCustomer = async (id) => {
    const [res, consentRes] = await Promise.all([
      fetch(`${API_URL}/api/crm/customers/${id}`, { headers }),
      fetch(`${API_URL}/api/crm/customers/${id}/marketing-consent`, { headers }),
    ]);
    if (res.ok) setSelected(await res.json());
    setConsentEvents(consentRes.ok ? await consentRes.json() : []);
    setConsentChoice('GRANTED');
    setConsentSource('customer_request');
    setConsentReference('');
    setConsentError(null);
  };

  const recordMarketingConsent = async (e) => {
    e.preventDefault();
    if (!selected) return;
    setSavingConsent(true);
    setConsentError(null);
    const commandId = window.crypto?.randomUUID?.()
      || 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
        const random = Math.floor(Math.random() * 16);
        return (char === 'x' ? random : ((random & 0x3) | 0x8)).toString(16);
      });
    try {
      const res = await fetch(`${API_URL}/api/crm/customers/${selected.id}/marketing-consent`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          choice: consentChoice,
          source: consentSource,
          evidence_reference: consentReference,
          expected_version: selected.marketing_consent_version || 0,
          command_id: commandId,
        }),
      });
      if (!res.ok) {
        const error = await res.json().catch(() => ({}));
        setConsentError(typeof error.detail === 'string' ? error.detail : 'Consent choice was not recorded. Refresh and retry.');
        return;
      }
      await openCustomer(selected.id);
    } catch {
      setConsentError('Network error. Refresh the customer before retrying the consent change.');
    } finally { setSavingConsent(false); }
  };

  const exportSegment = async (segment) => {
    const res = await fetch(`${API_URL}/api/crm/segments/export?segment=${segment}`, { headers });
    if (!res.ok) return;
    const text = await res.text();
    const url = URL.createObjectURL(new Blob([text], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = `crm-${segment}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const [campaign, setCampaign] = useState(null); // {segment, message} | null
  const [sending, setSending] = useState(false);
  const [campaignResult, setCampaignResult] = useState(null);
  const [campaignPreview, setCampaignPreview] = useState(null);
  const [campaignPreviewLoading, setCampaignPreviewLoading] = useState(false);
  const [campaignPreviewError, setCampaignPreviewError] = useState(null);
  const [campaignError, setCampaignError] = useState(null);
  const [previewRequest, setPreviewRequest] = useState(0);
  const [showHistory, setShowHistory] = useState(false);
  const [campaignHistory, setCampaignHistory] = useState([]);
  const campaignSegment = campaign?.segment;
  useEffect(() => {
    if (!campaignSegment) {
      setCampaignPreview(null);
      setCampaignPreviewError(null);
      return undefined;
    }
    const controller = new AbortController();
    setCampaignPreview(null);
    setCampaignPreviewError(null);
    setCampaignPreviewLoading(true);
    const query = new URLSearchParams({ segment: campaignSegment, days: '30', limit: '500' });
    fetch(`${API_URL}/api/crm/segments/preview?${query}`, { headers, signal: controller.signal })
      .then(async (res) => {
        if (!res.ok) throw new Error('Could not load the current consent-qualified audience.');
        return res.json();
      })
      .then(setCampaignPreview)
      .catch((error) => {
        if (error.name !== 'AbortError') setCampaignPreviewError(error.message || 'Audience preview failed.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setCampaignPreviewLoading(false);
      });
    return () => controller.abort();
  }, [campaignSegment, headers, previewRequest]);

  const openWinbackCampaign = () => {
    setCampaignResult(null);
    setCampaignError(null);
    setPreviewRequest((value) => value + 1);
    setCampaign({ segment: 'winback', message: 'Hi {name}, we miss you at our restaurant! Here’s 15% off your next visit. See you soon.' });
  };
  const openHistory = async () => {
    setShowHistory(true);
    const res = await fetch(`${API_URL}/api/crm/campaigns`, { headers });
    setCampaignHistory(res.ok ? await res.json() : []);
  };
  const sendCampaign = async () => {
    setSending(true); setCampaignResult(null); setCampaignError(null);
    try {
      const res = await fetch(`${API_URL}/api/crm/segments/send`, {
        method: 'POST', headers,
        body: JSON.stringify({ segment: campaign.segment, message: campaign.message, days: 30 }),
      });
      if (res.ok) setCampaignResult(await res.json());
      else {
        const error = await res.json().catch(() => ({}));
        setCampaignError(typeof error.detail === 'string' ? error.detail : 'Campaign could not be sent. Refresh the audience and try again.');
      }
    } catch {
      setCampaignError('Network error. Check campaign history before retrying.');
    } finally { setSending(false); }
  };

  const logInteraction = async (e) => {
    e.preventDefault();
    if (!selected) return;
    const res = await fetch(`${API_URL}/api/crm/customers/${selected.id}/interactions`, {
      method: 'POST', headers, body: JSON.stringify(interaction),
    });
    if (res.ok) {
      setInteraction({ type: 'note', summary: '', channel: '' });
      openCustomer(selected.id);
    }
  };

  if (!orgId && !loading) {
    return <div className="max-w-6xl mx-auto p-8 text-ink-muted" data-testid="crm-no-org">No active organization. Please log in.</div>;
  }

  return (
    <div className="min-h-screen bg-surface-muted py-8" data-testid="crm-page">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-overline text-accent-500 mb-1">Guests & Customers</div>
            <h1 className="font-display text-3xl text-ink">CRM</h1>
          </div>
          <button onClick={() => { setFormError(null); setShowForm(true); }} data-testid="crm-add-customer-btn"
            className="flex items-center gap-2 bg-primary-500 text-white px-4 py-2.5 rounded-md text-sm font-medium hover:bg-primary-600 motion-organic">
            <UserPlus className="w-4 h-4" /> Add Customer
          </button>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <StatCard label="Customers" value={stats.total_customers} icon={Users} />
            <StatCard label="Marketing Opted-In" value={stats.marketing_opted_in} icon={Star} />
            <StatCard label="Interactions" value={stats.total_interactions} icon={MessageCircle} />
            <StatCard label="Top Spend" value={`£${(stats.top_spenders?.[0]?.total_spend || 0).toFixed(0)}`} icon={Star} />
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="crm-campaigns-bar">
          <span className="text-xs uppercase tracking-wide text-ink-muted mr-1">Campaigns:</span>
          <button onClick={() => exportSegment('vip')} data-testid="crm-export-vip" className="text-xs px-3 py-1.5 rounded-md border border-accent-500 text-accent-600 hover:bg-accent-50 font-medium">Export VIPs</button>
          <button onClick={() => exportSegment('winback')} data-testid="crm-export-winback" className="text-xs px-3 py-1.5 rounded-md border border-warn text-warn hover:bg-warn/10 font-medium">Win-back (30d)</button>
          <button onClick={() => exportSegment('opted_in')} data-testid="crm-export-optedin" className="text-xs px-3 py-1.5 rounded-md border border-primary-500 text-primary-600 hover:bg-primary-50 font-medium">Marketing list</button>
          <button onClick={openWinbackCampaign} data-testid="crm-send-winback" className="text-xs px-3 py-1.5 rounded-md bg-accent-500 text-white hover:bg-accent-600 font-medium">Send Win-back Offer</button>
          <button onClick={openHistory} data-testid="crm-campaign-history-btn" className="text-xs px-3 py-1.5 rounded-md border border-surface-border text-ink-muted hover:bg-surface-sunken font-medium">Campaign History</button>
        </div>

        <div className="relative mb-4 max-w-sm">
          <Search className="w-4 h-4 text-ink-muted absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email, phone"
            data-testid="crm-search-input"
            className="w-full pl-9 pr-3 py-2 border border-surface-border rounded-md text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-surface-border overflow-hidden">
          {loading ? (
            <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary-500" /></div>
          ) : customers.length === 0 ? (
            <div className="p-10 text-center text-ink-muted" data-testid="crm-empty">No customers yet. Add your first guest.</div>
          ) : (
            <table className="w-full text-sm" data-testid="crm-customers-table">
              <thead className="bg-surface-sunken text-ink-muted text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-left px-4 py-3">Contact</th>
                  <th className="text-left px-4 py-3">Tier</th>
                  <th className="text-left px-4 py-3">Tags</th>
                  <th className="text-right px-4 py-3">Spend</th>
                  <th className="text-right px-4 py-3">Visits</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {customers.map(c => (
                  <tr key={c.id} className="border-t border-surface-border hover:bg-primary-50/40" data-testid={`crm-row-${c.id}`}>
                    <td className="px-4 py-3 font-medium text-ink">{c.name}</td>
                    <td className="px-4 py-3 text-ink-muted">{c.email || c.phone || '—'}</td>
                    <td className="px-4 py-3">{c.tier ? <span className={`text-xs px-2 py-0.5 rounded ${c.tier === 'VIP' ? 'bg-accent-500 text-white' : c.tier === 'Gold' ? 'bg-warn/20 text-warn' : c.tier === 'Silver' ? 'bg-surface-sunken text-ink' : 'bg-primary-50 text-primary-700'}`}>{c.tier}</span> : <span className="text-ink-muted text-xs">—</span>}</td>
                    <td className="px-4 py-3">{(c.tags || []).map(t => <span key={t} className="inline-block bg-primary-50 text-primary-700 text-xs px-2 py-0.5 rounded mr-1">{t}</span>)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">£{(c.total_spend || 0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{c.visits || 0}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => openCustomer(c.id)} data-testid={`crm-view-${c.id}`}
                        className="text-primary-600 hover:underline text-xs font-medium">View</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Campaign history modal */}
      {showHistory && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="crm-history-modal" onClick={() => setShowHistory(false)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl text-ink">Campaign History</h2>
              <button onClick={() => setShowHistory(false)}><X className="w-5 h-5 text-ink-muted" /></button>
            </div>
            {campaignHistory.length === 0 ? (
              <div className="p-8 text-center text-ink-muted" data-testid="crm-history-empty">No campaigns sent yet.</div>
            ) : (
              <table className="w-full text-sm" data-testid="crm-history-table">
                <thead className="bg-surface-sunken text-ink-muted text-xs uppercase">
                  <tr><th className="text-left px-3 py-2">When</th><th className="text-left px-3 py-2">Segment</th><th className="text-left px-3 py-2">Message</th><th className="text-right px-3 py-2">To</th><th className="text-right px-3 py-2">Sent</th><th className="text-right px-3 py-2">Skip</th><th className="text-right px-3 py-2">Fail</th></tr>
                </thead>
                <tbody>
                  {campaignHistory.map(c => (
                    <tr key={c.id} className="border-t border-surface-border" data-testid={`crm-history-row-${c.id}`}>
                      <td className="px-3 py-2 text-ink-muted whitespace-nowrap">{new Date(c.created_at).toLocaleDateString()}</td>
                      <td className="px-3 py-2"><span className="text-xs px-2 py-0.5 rounded bg-primary-50 text-primary-700">{c.segment}</span></td>
                      <td className="px-3 py-2 text-ink truncate max-w-[200px]" title={c.message}>{c.message}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{c.recipients}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-ok">{c.sent}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-warn">{c.skipped}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-danger">{c.failed}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Win-back / campaign modal */}
      {campaign && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="crm-campaign-modal">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl text-ink">Send Win-back Offer</h2>
              <button onClick={() => setCampaign(null)}><X className="w-5 h-5 text-ink-muted" /></button>
            </div>
            {campaignResult ? (
              <div className="space-y-3" data-testid="crm-campaign-result">
                <div className="text-sm text-ink">Campaign complete — {campaignResult.recipients} recipient(s): <b>{campaignResult.sent} sent</b>, {campaignResult.skipped} skipped, {campaignResult.failed} failed.</div>
                {campaignResult.note && <div className="bg-warn/10 text-warn text-sm rounded-md px-3 py-2">{campaignResult.note}</div>}
                {campaignPreview && campaignPreview.eligible_with_consent_and_phone !== campaignResult.recipients && <div className="text-xs text-ink-muted">The audience changed after preview. FoodOps rechecked consent and phone availability immediately before sending.</div>}
                <button onClick={() => setCampaign(null)} className="w-full bg-primary-500 text-white py-2.5 rounded-md text-sm font-medium">Done</button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="text-xs text-ink-muted">Segment: guests who visited but not in the last 30 days. Use <code>{'{name}'}</code> to personalise.</div>
                {campaignPreviewLoading && <div className="text-sm text-ink-muted" role="status">Checking consent-qualified audience…</div>}
                {campaignPreviewError && <div className="space-y-2" role="alert"><div className="text-sm text-danger">{campaignPreviewError}</div><button onClick={() => setPreviewRequest((value) => value + 1)} className="text-sm underline">Refresh audience preview</button></div>}
                {campaignPreview && <div className="border border-surface-border rounded-md p-3 text-sm" data-testid="crm-campaign-preview">
                  <div><b>{campaignPreview.eligible_with_consent_and_phone}</b> opted-in guests with a phone number are eligible (up to {campaignPreview.limit}).</div>
                  <div className="text-xs text-ink-muted mt-1">{campaignPreview.excluded_without_explicit_consent} excluded without explicit marketing consent; {campaignPreview.excluded_without_recorded_consent || 0} legacy grants lack a source record; {campaignPreview.excluded_recorded_opt_in_without_phone ?? campaignPreview.excluded_opted_in_without_phone ?? 0} recorded grants have no phone. Counts refresh at send time.</div>
                </div>}
                {campaignError && <div className="text-sm text-danger" role="alert">{campaignError}</div>}
                <textarea value={campaign.message} onChange={e => setCampaign({ ...campaign, message: e.target.value })} rows={4} data-testid="crm-campaign-message" className="w-full px-3 py-2 border border-surface-border rounded-md text-sm" />
                <button onClick={sendCampaign} disabled={sending || campaignPreviewLoading || !campaignPreview || campaignPreview.eligible_with_consent_and_phone < 1 || !campaign.message.trim()} data-testid="crm-campaign-send" className="w-full bg-accent-500 text-white py-2.5 rounded-md text-sm font-medium hover:bg-accent-600 disabled:opacity-50">{sending ? 'Sending…' : campaignPreview ? `Send to ${campaignPreview.eligible_with_consent_and_phone} opted-in guests` : 'Send via WhatsApp'}</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add customer modal */}
      {showForm && (        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="crm-form-modal">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl text-ink">New Customer</h2>
              <button onClick={() => setShowForm(false)}><X className="w-5 h-5 text-ink-muted" /></button>
            </div>
            <form onSubmit={createCustomer} className="space-y-3">
              {formError && <div className="bg-danger/10 text-danger text-sm rounded-md px-3 py-2" data-testid="crm-form-error">{formError}</div>}
              <input required placeholder="Full name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                data-testid="crm-form-name" className="w-full px-3 py-2 border border-surface-border rounded-md text-sm" />
              <input type="email" placeholder="Email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                data-testid="crm-form-email" className="w-full px-3 py-2 border border-surface-border rounded-md text-sm" />
              <input placeholder="Phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
                data-testid="crm-form-phone" className="w-full px-3 py-2 border border-surface-border rounded-md text-sm" />
              <input placeholder="Tags (comma-separated)" value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })}
                data-testid="crm-form-tags" className="w-full px-3 py-2 border border-surface-border rounded-md text-sm" />
              <textarea placeholder="Notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                data-testid="crm-form-notes" className="w-full px-3 py-2 border border-surface-border rounded-md text-sm" rows={2} />
              <button type="submit" disabled={saving} data-testid="crm-form-submit"
                className="w-full bg-primary-500 text-white py-2.5 rounded-md text-sm font-medium hover:bg-primary-600 disabled:opacity-50">
                {saving ? 'Saving…' : 'Create Customer'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Customer detail drawer */}
      {selected && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-end z-50" data-testid="crm-detail-drawer" onClick={() => setSelected(null)}>
          <div className="bg-white h-full w-full max-w-md p-6 overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl text-ink">{selected.name}</h2>
              <button onClick={() => setSelected(null)}><X className="w-5 h-5 text-ink-muted" /></button>
            </div>
            <div className="text-sm text-ink-muted space-y-1 mb-6">
              <div>{selected.email || '—'} · {selected.phone || '—'}</div>
              <div>Spend £{(selected.total_spend || 0).toFixed(2)} · {selected.visits || 0} visits · {selected.loyalty_points || 0} pts</div>
              {selected.notes && <div className="text-ink pt-2">{selected.notes}</div>}
            </div>

            <section className="border-y border-surface-border py-4 mb-5" aria-labelledby="crm-consent-heading">
              <h3 id="crm-consent-heading" className="text-sm font-semibold text-ink mb-2">Marketing consent</h3>
              <p className="text-xs text-ink-muted mb-3" data-testid="crm-consent-current">
                {selected.marketing_opt_in && selected.marketing_consent_event_id
                  ? 'Grant recorded; source is staff-reported and unverified.'
                  : selected.marketing_opt_in
                    ? 'Legacy grant has no recorded source and is excluded from campaigns.'
                    : selected.marketing_consent_event_id
                      ? 'Consent withdrawn.'
                      : 'No consent grant recorded.'}
              </p>
              <form onSubmit={recordMarketingConsent} className="space-y-2">
                <select aria-label="Consent choice" value={consentChoice} onChange={e => setConsentChoice(e.target.value)} className="w-full px-3 py-2 border border-surface-border rounded-md text-sm">
                  <option value="GRANTED">Customer granted consent</option>
                  <option value="WITHDRAWN">Customer withdrew consent</option>
                </select>
                <select aria-label="Consent source" value={consentSource} onChange={e => setConsentSource(e.target.value)} className="w-full px-3 py-2 border border-surface-border rounded-md text-sm">
                  {['customer_request', 'paper_form', 'web_form', 'pos', 'email', 'phone', 'verbal', 'other'].map(source => <option key={source} value={source}>{source.replaceAll('_', ' ')}</option>)}
                </select>
                <input aria-label="Consent evidence reference" value={consentReference} onChange={e => setConsentReference(e.target.value)} maxLength={500} placeholder="Evidence reference (optional)" className="w-full px-3 py-2 border border-surface-border rounded-md text-sm" />
                <p className="text-xs text-ink-muted">This records the staff-entered source and choice; FoodOps does not verify attached source material.</p>
                {consentError && <div className="text-sm text-danger" role="alert">{consentError}</div>}
                <button type="submit" disabled={savingConsent} data-testid="crm-consent-record" className="w-full border border-primary-500 text-primary-700 py-2 rounded-md text-sm font-medium disabled:opacity-50">{savingConsent ? 'Recording…' : 'Record consent choice'}</button>
              </form>
              <ol className="mt-3 space-y-2" data-testid="crm-consent-history">
                {consentEvents.map(event => <li key={event.id} className="text-xs text-ink-muted border-l-2 border-surface-border pl-2">
                  {event.choice === 'GRANTED' ? 'Granted' : 'Withdrawn'} via {event.source.replaceAll('_', ' ')} · {new Date(event.recorded_at).toLocaleString()} · {event.recording_status.toLowerCase().replaceAll('_', ' ')}
                  {event.evidence_reference && <div>Reference: {event.evidence_reference}</div>}
                </li>)}
              </ol>
            </section>

            <h3 className="text-sm font-semibold text-ink mb-2">Log interaction</h3>
            <form onSubmit={logInteraction} className="space-y-2 mb-6">
              <select value={interaction.type} onChange={e => setInteraction({ ...interaction, type: e.target.value })}
                data-testid="crm-interaction-type" className="w-full px-3 py-2 border border-surface-border rounded-md text-sm">
                {['note', 'call', 'email', 'visit', 'complaint', 'feedback'].map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <input required placeholder="Summary" value={interaction.summary} onChange={e => setInteraction({ ...interaction, summary: e.target.value })}
                data-testid="crm-interaction-summary" className="w-full px-3 py-2 border border-surface-border rounded-md text-sm" />
              <button type="submit" data-testid="crm-interaction-submit"
                className="w-full bg-ink text-white py-2 rounded-md text-sm font-medium hover:opacity-90">Add</button>
            </form>

            <h3 className="text-sm font-semibold text-ink mb-2">Timeline</h3>
            <div className="space-y-2">
              {(selected.interactions || []).length === 0 && <div className="text-sm text-ink-muted">No interactions yet.</div>}
              {(selected.interactions || []).map(i => (
                <div key={i.id} className="border border-surface-border rounded-md p-3 text-sm">
                  <div className="flex justify-between">
                    <span className="font-medium text-primary-700 capitalize">{i.type}</span>
                    <span className="text-xs text-ink-muted">{new Date(i.created_at).toLocaleString()}</span>
                  </div>
                  <div className="text-ink">{i.summary}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CRM;
